import { create } from 'zustand';
import { api } from '../services/api';
import type { Memory } from '../types';

interface MemoriesState {
  memories: Memory[];
  tags: string[];
  isLoading: boolean;
  error: string | null;
  fetchMemories: (params?: { tag?: string; query?: string }) => Promise<void>;
  fetchTags: () => Promise<void>;
  createMemory: (data: Omit<Memory, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateMemory: (id: string, data: Partial<Memory>) => Promise<void>;
  deleteMemory: (id: string) => Promise<void>;
}

export const useMemoriesStore = create<MemoriesState>((set) => ({
  memories: [],
  tags: [],
  isLoading: false,
  error: null,

  fetchMemories: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const query = new URLSearchParams();
      if (params?.tag) query.set('tag', params.tag);
      if (params?.query) query.set('q', params.query);
      const memories = await api.get<Memory[]>(`/api/memories?${query}`);
      set({ memories, isLoading: false });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false });
    }
  },

  fetchTags: async () => {
    const tags = await api.get<string[]>('/api/memories/tags');
    set({ tags });
  },

  createMemory: async (data) => {
    const memory = await api.post<Memory>('/api/memories', data);
    set(state => ({ memories: [memory, ...state.memories] }));
  },

  updateMemory: async (id, data) => {
    await api.patch(`/api/memories/${id}`, data);
    set(state => ({ memories: state.memories.map(m => m.id === id ? { ...m, ...data } : m) }));
  },

  deleteMemory: async (id) => {
    await api.delete(`/api/memories/${id}`);
    set(state => ({ memories: state.memories.filter(m => m.id !== id) }));
  },
}));